import { useEffect, useRef, useState } from 'react';
import { AppleGooglePlayTypes, PaymentButtons, PaymentType } from '@msc-b2c/interfaces/types';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  closeLoader,
  CreateBooking,
  createBookingRequestBody,
  getAllowedCardNetworksPaymentButton,
  getAllowedPaymentButtons,
  getDiscountsArray,
  getErrorMessage,
  RootState,
  setBookingResult,
  setIsErrorModalOpen,
  trackPageView,
  trackPaymentFailed,
  trackPurchase,
  trackPurchaseFailed,
  useScript,
} from '@msc-b2c/shared-utils';
import { IBookingResponse } from '@msc-b2c/interfaces/interfaces';

const DEFAULT_OPTIONS = {
  tokenOnly: false,
  autoSettle: false,
  useGooglePay: getAllowedPaymentButtons().includes(PaymentButtons.GOOGLE_PAY),
  useApplePay: getAllowedPaymentButtons().includes(PaymentButtons.APPLE_PAY),
  googlePayConfiguration: {
    buttonType: 'long',
    buttonStyle: 'black',
    merchantId: '{googlePayMerchantId}',
  },
  applePayConfiguration: {
    buttonType: 'plain',
    buttonStyle: 'black',
  },
};

export const useAppleOrGooglePay = ({
  merchantId,
  gpayMerchantId,
  datatransPaymentButtonUrl,
  merchantName,
  options,
  payment,
  paymentType,
}: AppleGooglePlayTypes) => {
  const [error, setError] = useState('');
  const [isLoaded, hasError] = useScript({ src: datatransPaymentButtonUrl });
  const [bookingReqLoading, setBookingReqLoading] = useState(false);
  const payButton = useRef(null);
  const paymentButton = window?.PaymentButton;

  const {
    firstCabinSelections,
    secondCabinSelections,
    selectedDiningOption,
    selectedInsurance,
    eCouponCode,
    emergencyContact,
  } = useSelector((state: RootState) => state.bookingStepsStore);
  const { cruiseID, destinationairportcode, departureairportcode, date } = useSelector(
    (state: RootState) => state.bookingParams
  );
  const { occupancySecondCabinGuests } = useSelector((state: RootState) => state.occupancy);
  const { isApplicable } = useSelector((state: RootState) => state.promotions);
  const { firstCabinSelections: deckFirstSelections, secondCabinSelections: deckSecondelections } =
    useSelector((state: RootState) => state.deckView);
  const { selectedAirfare } = useSelector((state: RootState) => state.flightData);

  const allowedCardNetworks = getAllowedCardNetworksPaymentButton();
  const defaultOptions = {
    ...DEFAULT_OPTIONS,
    allowedCardNetworks,
    googlePayConfiguration: {
      ...DEFAULT_OPTIONS.googlePayConfiguration,
      merchantId: gpayMerchantId || '{googlePayMerchantId}',
    },
  };

  const getPassengerType = () => {
    if (!isApplicable) return '';

    const firstCabinPassengerType =
      firstCabinSelections?.discountsToBeApplied?.passengerType ?? undefined;

    if (firstCabinSelections?.selectedCabinPreferenceObj?.cabinPreference === 'REAL') {
      return (
        firstCabinPassengerType ??
        firstCabinSelections?.selectedCategory?.passengerType ??
        secondCabinSelections?.selectedCategory?.passengerType ??
        ''
      );
    } else {
      return (
        firstCabinPassengerType ??
        firstCabinSelections?.selectedCabinPreferenceObj?.lowestPricedCategory?.passengerType ??
        secondCabinSelections?.selectedCabinPreferenceObj?.lowestPricedCategory?.passengerType ??
        ''
      );
    }
  };

  const getDiscountCodesArrayFirstCabin = () => {
    return getDiscountsArray(firstCabinSelections, secondCabinSelections, isApplicable, true)
      .discountCodesArrayFirstCabin;
  };

  const getDiscountCodesArraySecondCabin = () => {
    return getDiscountsArray(firstCabinSelections, secondCabinSelections, isApplicable, true)
      .discountCodesArraySecondCabin;
  };

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const cleanupPaymentButton = () => {
    if (paymentButton) {
      try {
        paymentButton.destroy();
      } catch (err) {
        console.log('Unmount error', err);
      }
    }
  };

  useEffect(() => {
    if (isLoaded && !hasError && paymentButton && payButton && cruiseID) {
      setError('');
      const handleInit = () => {
        paymentButton.create(payButton.current, payment);
      };

      const handleError = (err: unknown) => {
        dispatch(trackPaymentFailed(err));
        console.log('ERROR on payment btn', err);
        const message = getErrorMessage(err);
        setError(message);
      };

      const handleAuth = (response: any) => {
        console.log('response: ', response);

        if (response?.transactionId) {
          // Create the booking request body
          const bookingType = paymentType === PaymentType.TOTALTRAVEL ? 'FULL' : 'DEPOSIT';
          const couponCode = eCouponCode && !eCouponCode.isMultiusage ? eCouponCode.code : '';

          const bookingsReq = createBookingRequestBody(
            getPassengerType(),
            getDiscountCodesArrayFirstCabin(),
            getDiscountCodesArraySecondCabin(),
            firstCabinSelections,
            secondCabinSelections,
            deckFirstSelections.selectedCabinId,
            deckSecondelections.selectedCabinId,
            cruiseID,
            bookingType,
            couponCode,
            occupancySecondCabinGuests,
            selectedDiningOption,
            selectedInsurance || [],
            selectedAirfare || undefined,
            destinationairportcode ?? '',
            departureairportcode ?? '',
            date ?? '',
            null, // webform
            emergencyContact,
            response.transactionId
          );

          setBookingReqLoading(true);

          const createBooking = new CreateBooking(false, false, bookingsReq, undefined);
          let bookingRes: IBookingResponse;
          createBooking
            .sendData()
            .then((res) => {
              if (res) {
                bookingRes = res;
                dispatch(
                  setBookingResult({
                    bookingType,
                    bookingNumber: res.bookingNumber,
                    totalPaymentReceived: res.totalPaymentReceived,
                    ...(res._warnings &&
                      res._warnings.length > 0 && {
                        warnings: res._warnings,
                      }),
                  })
                );
                closeLoader('.loader');
                // Navigate to Thank You page
                navigate(`/thankyou${location.search}`);
              } else {
                console.log('Booking not found');
                dispatch(setIsErrorModalOpen(true));
              }
            })
            .catch(() => {
              dispatch(trackPaymentFailed(response?.data));
              dispatch(setIsErrorModalOpen(true));
            })
            .finally(() => {
              if (bookingRes?.bookingNumber && !bookingRes?._warnings) {
                dispatch(trackPageView());
                dispatch(trackPurchase(bookingRes));
              } else if (bookingRes?.bookingNumber && bookingRes?.bookingNumber > 0) {
                dispatch(trackPageView());
                dispatch(trackPurchaseFailed(bookingRes));
              }

              setBookingReqLoading(false);
            });
        }
      };

      // Add event listeners
      paymentButton.on('init', handleInit);
      paymentButton.on('error', handleError);
      paymentButton.on('authorization', handleAuth);

      // Initialize payment button with options
      paymentButton.init({
        ...defaultOptions,
        ...options,
        merchantId,
        merchantName,
      });

      // Cleanup function
      return () => {
        // Check if paymentButton still exists before trying to destroy it
        if (paymentButton) {
          console.log('Destroying payment button');
          paymentButton.off('init', handleInit);
          paymentButton.off('error', handleError);
          paymentButton.off('authorization', handleAuth);

          console.log('paymentButton', paymentButton);
          try {
            cleanupPaymentButton();
          } catch (error) {
            console.error('Error during destroy:', error);
          }
        }
      };
    }

    return () => {};
  }, [isLoaded, hasError, paymentButton, payButton, eCouponCode.code]);

  return { payButton, bookingReqLoading, error };
};
